import { Button, FieldCheckbox, FieldInput, FieldSelect, FieldTextarea, Form, FormSubmit } from "@qw/ui";
import { z } from "zod";

interface PlaybookOption {
  id: string;
  name: string;
}

interface ClassificationFormValues {
  id?: string;
  label: string;
  description?: string;
  associatedPlaybookId: string | null;
  autoRunPlaybook?: boolean;
}

interface ClassificationFormProps {
  mode: "create" | "edit";
  initial: ClassificationFormValues;
  playbookOptions: PlaybookOption[];
  submitLabel: string;
  disabled?: boolean;
  onSubmit?: (values: ClassificationFormValues) => Promise<void> | void;
  onCancel?: () => void;
  resetAfterSubmit?: boolean;
  canAutoRunPlaybook?: boolean;
  renderFieldsOnly?: boolean;
  userClassifications?: Array<{ id: string; label: string }>;
}

export const createClassificationValidationSchema = (
  mode: "create" | "edit",
  userClassifications: Array<{ id: string; label: string }> = [],
  currentId?: string
) => {
  return z.object({
    label: z
      .string()
      .min(1, { message: "Label is required" })
      .refine(
        (label) => {
          const trimmedLabel = label.trim();
          return mode === "create"
            ? !userClassifications.some((c) => c.label.toLowerCase() === trimmedLabel.toLowerCase())
            : !userClassifications.some(
                (existing) => existing.id !== currentId && existing.label.toLowerCase() === trimmedLabel.toLowerCase()
              );
        },
        {
          message: "A classification with this label already exists. Each classification label needs to be unique.",
        }
      ),
    description: z.string().optional(),
    associatedPlaybookId: z.string().optional(),
    autoRunPlaybook: z.boolean().optional(),
  });
};

export const ClassificationForm = ({
  mode,
  initial,
  playbookOptions,
  submitLabel,
  disabled,
  onSubmit,
  onCancel,
  resetAfterSubmit,
  canAutoRunPlaybook,
  renderFieldsOnly = false,
  userClassifications = [],
}: ClassificationFormProps) => {
  const NONE_VALUE = "__none__";

  const defaultValues = {
    id: initial.id,
    label: initial.label ?? "",
    description: initial.description ?? "",
    associatedPlaybookId: initial.associatedPlaybookId ?? NONE_VALUE,
    autoRunPlaybook: initial.autoRunPlaybook ?? false,
  } as const;

  const validationSchema =
    mode === "edit"
      ? z.object({
          id: z.string().min(1),
          ...createClassificationValidationSchema(mode, userClassifications, initial.id).shape,
        })
      : createClassificationValidationSchema(mode, userClassifications, initial.id);

  const formFields = (
    <>
      <FieldInput label="Label" name="label" className="relative focus-within:z-10 w-full" />
      <FieldTextarea
        label="Description"
        name="description"
        description="The description is used by Alby to decide how to classify the email. Only one classification will be chosen."
        className="relative focus-within:z-10 w-full"
      />
      <FieldSelect
        label="Associated playbook"
        name="associatedPlaybookId"
        placeholder="None"
        className="min-w-[280px]"
        options={[
          { type: "label", label: "Select a playbook" },
          { label: "None", value: NONE_VALUE },
          ...playbookOptions.map((p) => ({ label: p.name, value: p.id })),
        ]}
      />
      {canAutoRunPlaybook && (
        <div className="border border-muted rounded-sm p-2">
          <FieldCheckbox name="autoRunPlaybook" label="Auto-run playbook" />
        </div>
      )}
      {!renderFieldsOnly && (
        <>
          <FormSubmit className="self-end shrink-0" disabled={disabled}>
            {submitLabel}
          </FormSubmit>
          {onCancel && (
            <Button className="self-end shrink-0" onClick={onCancel} type="button" variant="ghost" size="sm">
              Cancel
            </Button>
          )}
        </>
      )}
    </>
  );

  if (renderFieldsOnly) {
    return formFields;
  }

  return (
    <Form
      defaultValues={defaultValues}
      validationSchema={validationSchema}
      onSubmit={async (
        fd: {
          id?: string;
          label: string;
          description?: string;
          associatedPlaybookId?: string;
          autoRunPlaybook?: boolean;
        },
        methods
      ) => {
        const normalized: ClassificationFormValues = {
          id: fd.id,
          label: fd.label,
          description: fd.description ?? "",
          associatedPlaybookId: fd.associatedPlaybookId === NONE_VALUE ? null : (fd.associatedPlaybookId ?? null),
          autoRunPlaybook: canAutoRunPlaybook ? Boolean(fd.autoRunPlaybook) : undefined,
        };

        await onSubmit?.(normalized);

        if (resetAfterSubmit) {
          methods.reset({ label: "", description: "", associatedPlaybookId: NONE_VALUE });
        }
      }}
    >
      {formFields}
    </Form>
  );
};
